{
	"seo": {
		"title": "StudyHub — a course built from your own materials",
		"description": "StudyHub builds a course from your materials or from open sources and walks every topic through three steps: understand, check yourself, close the gaps.",
		"descriptionShort": "A course built from your materials: every topic goes through three steps — understand, check yourself, close the gaps."
	},
	"header": {
		"login": "Sign in",
		"signup": "Try it",
		"help": "Help",
		"navFeatures": "Features",
		"navHow": "How it works",
		"navMethod": "Method",
		"navPricing": "Pricing",
		"navFaq": "FAQ"
	},
	"hero": {
		"eyebrow": "A course built around you",
		"title": "Master any material — all the way.",
		"lead": "StudyHub builds a course from your own materials or from open sources, splits it into topics and takes every topic to mastery in three steps.",
		"method": {
			"hint": "Every topic goes through three steps"
		},
		"ctaPrimary": "Start free",
		"ctaSecondary": "How it works",
		"checks": {
			"trial": "14 days of Plus on us",
			"noCard": "No credit card"
		},
		"preview": {
			"title": "Lecture 12 — Cellular respiration",
			"status": "ready",
			"meta": "91 minutes · ready 47 sec ago",
			"sectionTitle": "1. Stages of cellular respiration",
			"steps": {
				"s1": "Glycolysis — glucose breakdown in the cytoplasm (2 ATP, 2 NADH)",
				"s2": "Krebs cycle — pyruvate oxidation in the mitochondrial matrix",
				"s3": "Electron transport chain — synthesis of most ATP"
			},
			"aiHint": "AI hint: exams often ask about the total ATP yield (≈30–32). I added it to card #7.",
			"source": "Source · PDF, 14 pp.",
			"cards": {
				"counter": "Card 7 of 24",
				"frontLabel": "Question",
				"front": "How much ATP does full oxidation of one glucose molecule yield?",
				"backLabel": "Answer",
				"back": "≈30–32 ATP (glycolysis + Krebs cycle + electron transport chain)"
			},
			"quiz": {
				"counter": "Question 3 of 15",
				"question": "Where does the Krebs cycle take place?",
				"opt1": "In the cytoplasm",
				"opt2": "In the mitochondrial matrix",
				"opt3": "On the ribosomes"
			},
			"podcast": {
				"title": "Lecture audio recap",
				"host": "2 voices · dialogue",
				"elapsed": "3:12",
				"duration": "8:42"
			},
			"tutor": {
				"q": "Explain the difference between glycolysis and the Krebs cycle",
				"a": "Glycolysis runs in the cytoplasm without oxygen and yields 2 ATP, while the Krebs cycle runs in the mitochondrial matrix, oxidising pyruvate to CO₂ and feeding the electron transport chain."
			}
		},
		"ctaPrimaryNoSale": "Get started"
	},
	"method": {
		"eyebrow": "Method",
		"note": "Practice testing · spaced repetition",
		"title": "Why these three steps",
		"lead": "Rereading and highlighting are the most popular and the weakest ways to study. The method is built from the two techniques that reviews rate highest.",
		"step1": {
			"body": "The material becomes your footing: notes, audio, a diagram, a glossary. The step is short — it prepares the ground for testing."
		},
		"step2": {
			"body": "Flashcards and a quiz that make you retrieve from memory. Before answering you rate your confidence — that changes the review."
		},
		"step3": {
			"body": "The quiz shows exactly where the gap is. From there the system works on the gap, not on the whole topic."
		},
		"link": "Research and references — on the Method page",
		"figure": {
			"title": "What stays in memory",
			"legendRoute": "Topic route",
			"legendReread": "Rereading",
			"delta": "the gap — what stayed",
			"axisRepeat": "scheduled reviews",
			"alt": "Diagram: a three-step topic route leaves noticeably more in memory than rereading"
		}
	},
	"course": {
		"eyebrow": "Course",
		"note": "Materials · the web · catalogue",
		"title": "A course can come from anything",
		"lead": "Upload your materials, build a course on a topic from open sources, or take a ready-made programme. A course splits into topics, a topic into three steps.",
		"paths": {
			"own": {
				"title": "From your materials",
				"body": "PDFs, documents, spreadsheets, slides, photos, audio and video, YouTube, web pages. Up to 5 GB on Plus.",
				"bodyNoSale": "PDFs, documents, spreadsheets, slides, photos, audio and video, YouTube, web pages."
			},
			"web": {
				"title": "From the web",
				"body": "Describe the topic — StudyHub finds the sources, builds a programme from them and lays it out topic by topic."
			},
			"catalog": {
				"title": "From the catalogue",
				"body": "Ready-made programmes: EGE, OGE, SAT, AP, A-Level, GCSE, Abitur, Bac, UNT, JEE, IELTS, TOEFL."
			}
		},
		"goal": "Tell it why you are studying: “score 80+ on the exam” becomes the course goal — depth, examples and questions are tuned to it.",
		"sources": "Sources stay visible in the course: notes and tutor answers point to the exact fragment.",
		"figure": {
			"alt": "Diagram: materials, the web and the catalogue converge into one course, which splits into topics"
		}
	},
	"topicRoute": {
		"eyebrow": "Topic route",
		"note": "Understand · check yourself · close the gaps",
		"title": "How a single topic runs",
		"lead": "Steps unlock in order. Until the topic is tested, the third step stays locked — and that matters more than it sounds.",
		"checkpoint": "A topic is confirmed by a checkpoint quiz. Not enough — another round on the misses; “Finish” is always available.",
		"step1": {
			"items": [
				"Topic notes",
				"A podcast, a lecture or a quick recap",
				"Topic glossary",
				"Mind map",
				"Infographic",
				"Your own note"
			]
		},
		"step2": {
			"items": [
				"Flashcards of six types with spaced repetition",
				"A quiz with seven question types",
				"A confidence rating before every answer",
				"A walkthrough of each answer right after the quiz"
			]
		},
		"step3": {
			"items": [
				"The gaps the quiz found",
				"Voice tutor in a 15-minute session",
				"Tutor in chat",
				"A walkthrough with a character",
				"Another quiz on the gaps",
				"Flashcards on the gaps",
				"A note on the gaps",
				"Reread the section",
				"Checkpoint quiz"
			]
		}
	},
	"tutor": {
		"eyebrow": "Tutor",
		"note": "Screen context · source citations",
		"title": "The tutor knows which screen you are on",
		"lead": "It sees your current topic and your materials, shows its chain of reasoning and answers with links to the source — not “generally, from the internet”.",
		"facts": {
			"everywhere": "Right there on any screen — in the side panel, and on its own screen when the conversation gets long.",
			"voice": "A voice session that follows a lesson plan — or a focused one on a single gap.",
			"build": "It can build a quiz, flashcards, a diagram or audio from the conversation on the spot."
		}
	},
	"characters": {
		"eyebrow": "Characters",
		"note": "A different voice · the same sources",
		"title": "Work through a topic with someone you want to listen to",
		"lead": "Same material, different company. A character walks you through your own sources — it changes how things are explained, not how they look.",
		"examples": {
			"wizard": "Discuss quantum mechanics with a wizard",
			"cartoon": "Work through chemistry with a favourite cartoon hero",
			"philosopher": "Ask an ancient philosopher about microprocessors",
			"detective": "Go through history with a fictional detective"
		},
		"catalog": "Ready-made characters live in the catalogue; it is shared with CharacterHub and connects to your account.",
		"disclaimer": "A character changes the manner of explanation. The factual part stays tied to your sources."
	},
	"rhythm": {
		"eyebrow": "Rhythm",
		"note": "Plan · calendar · reminders",
		"title": "Study runs on a schedule, not on mood",
		"lead": "Tell it how many minutes a day you can give — it lays topics, reviews and deadlines across your days. Missed one? It reshuffles.",
		"facts": {
			"budget": "One plan across all courses, one shared daily budget",
			"calendar": "Calendar: sessions, reviews, deadlines",
			"reminders": "Reminders in your own time zone",
			"recording": "Lecture recording keeps running with the screen locked"
		},
		"figure": {
			"alt": "Diagram: a week of sessions and reviews laid out inside the daily minute budget"
		}
	},
	"toolbox": {
		"eyebrow": "Everything inside",
		"note": "Grouped the way the app groups it",
		"title": "Everything inside",
		"lead": "Tools are grouped the way they are grouped in the app — by what you are doing.",
		"groups": {
			"library": "Library and sources",
			"libraryHint": "Materials, folders, search, glossary, lecture recording"
		}
	},
	"demo": {
		"frame": {
			"topicRoute": "Topic route",
			"course": "Course",
			"tutor": "Tutor",
			"characters": "Walkthrough with a character",
			"step1": "Step 1 · understand",
			"step2": "Step 2 · check yourself",
			"step3": "Step 3 · close the gaps"
		},
		"topic": {
			"title": "The Krebs cycle",
			"meta": "Biochemistry · topic 3 of 6 · 18 min",
			"progress": "2/3",
			"step1": {
				"sub": "Notes · audio · topic glossary",
				"state": "done"
			},
			"step2": {
				"sub": "Flashcards and a quiz with a confidence scale",
				"state": "8/12"
			},
			"step3": {
				"sub": "Unlocks after the check — built from your misses",
				"state": "locked"
			}
		},
		"citation": "Ch. 4 · p. 142",
		"citationNote": "Every answer comes with a link to your source",
		"course": {
			"title": "Biochemistry · cellular respiration",
			"meta": "6 topics · 2 of 6 mastered",
			"topics": [
				"Glycolysis",
				"Oxidative decarboxylation",
				"The Krebs cycle",
				"The respiratory chain",
				"Oxidative phosphorylation",
				"Regulation and pathology"
			]
		},
		"confidence": {
			"label": "How sure are you?",
			"unsure": "Not sure",
			"doubt": "Doubtful",
			"sure": "Confident"
		},
		"gap": {
			"title": "Gap found by the quiz",
			"body": "You mix up substrate-level phosphorylation with oxidative phosphorylation",
			"tools": [
				"By voice · 15 min",
				"Flashcards on the gap",
				"Another quiz"
			]
		},
		"tutor": {
			"context": "Topic: the Krebs cycle",
			"thinking": "Checking against your notes",
			"q": "Why does the Krebs cycle stall without oxygen?",
			"a": "The cycle itself consumes no oxygen. It stalls indirectly: without oxygen the respiratory chain cannot oxidise NADH back to NAD⁺, and the cycle runs out of carriers."
		},
		"characters": {
			"role": "Works through your own sources",
			"q": "Explain the Krebs cycle as an investigation",
			"a": "So: two carbon atoms have gone missing. Let us track them — acetyl-CoA enters the cycle, and at the exit we find two molecules of CO₂. The evidence matches."
		},
		"rhythm": {
			"days": [
				"Mon",
				"Tue",
				"Wed",
				"Thu",
				"Fri",
				"Sat",
				"Sun"
			],
			"norm": "Daily budget",
			"normValue": "45 of 60 min",
			"today": "Today",
			"legendLesson": "Session",
			"legendReview": "Review",
			"legendDeadline": "Deadline"
		}
	},
	"audiences": {
		"eyebrow": "Who it is for",
		"note": "School · university · profession",
		"title": "One tool — three audiences",
		"lead": "A school student preps for finals. A uni student — for the session. A pro — for certification. Same workspace, different context.",
		"tabs": {
			"school": "School",
			"university": "University",
			"pro": "Courses & pros"
		},
		"tags": {
			"notes": "Notes",
			"cards": "Flashcards",
			"quiz": "Quiz",
			"tutor": "Tutor"
		},
		"school": {
			"headline": "Preparing for graduation exams",
			"bullets": [
				"Record a class with auto-transcript and notes",
				"Recognise handwritten notes from a photo",
				"AI tutor explains hard topics at your level",
				"Study templates for your country's exam system"
			],
			"caseLabel": "Scenario",
			"caseTitle": "Biology finals prep",
			"caseBody": "8 textbooks and 3 YouTube courses → notes, review cards and a quiz across the whole syllabus. AI marks weak spots and tells you what to review today."
		},
		"university": {
			"headline": "Preparing for the session",
			"bullets": [
				"Lecture recording with auto-notes",
				"AI tutor knows your whole course, not one file",
				"Weak Spots after a quiz — what to review before the exam",
				"Study Plan by session dates — AI distributes the load"
			],
			"caseLabel": "Scenario",
			"caseTitle": "Colloquium prep",
			"caseBody": "12 lectures, 4 textbook chapters, 2 papers → a glossary of terms, quizzes on key topics and an audio recap for the commute. A complete study pack in one evening."
		},
		"pro": {
			"headline": "Learning alongside work",
			"bullets": [
				"AWS, Google Cloud, financial certifications",
				"Audio recap on a run or commute",
				"AI tutor explains at the right depth",
				"Calendar prep — AI accounts for your weekly hours"
			],
			"caseLabel": "Scenario",
			"caseTitle": "AWS Solutions Architect prep",
			"caseBody": "Official docs and video courses → structured notes, quizzes per exam domain and a Voice Tutor for hard topics. 30 minutes a day."
		}
	},
	"pricing": {
		"eyebrow": "Pricing",
		"note": "Free forever · Plus · Pro",
		"title": "Simple pricing",
		"countryHint": "Free forever. Plus — for active learners. Pro — for exam prep.",
		"popular": "Popular",
		"viewAll": "All features",
		"perMonth": "/mo",
		"free": {
			"name": "Free",
			"tagline": "To try things out",
			"features": [
				"5 AI generations a day",
				"Up to 10 materials, 100 MB",
				"Notes, flashcards, quizzes",
				"Mobile app + web"
			]
		},
		"plus": {
			"name": "Plus",
			"tagline": "For active learning",
			"features": [
				"50 AI generations a day",
				"Up to 100 materials, 5 GB",
				"Two-voice podcast",
				"Original file downloads",
				"Deep Research: 5 runs a month"
			]
		},
		"pro": {
			"name": "Pro",
			"tagline": "For exams and certifications",
			"features": [
				"Unlimited AI generations",
				"20 GB library with no cap on file count",
				"Group study",
				"Deep Research: 20 runs a month",
				"Early access to app updates"
			]
		}
	},
	"faq": {
		"eyebrow": "Questions",
		"note": "Method · data · pricing",
		"title": "Frequently asked questions",
		"items": {
			"method": {
				"q": "Why these three steps?",
				"a": "The order “understand → check yourself → close the gaps” is built around the two techniques that reviews of study methods rate highest for usefulness — practice testing and spaced repetition. The details, with sources, are on the Method page."
			},
			"ai": {
				"q": "How does the AI work?",
				"a": "Your uploaded materials are the foundation: AI parses them and builds notes, flashcards, quizzes and tutor answers on top of them. Context lives within your account."
			},
			"dataResidency": {
				"q": "What happens to my data?",
				"a": "Your materials are visible only to you and are used solely to help you learn — to generate notes, flashcards, quizzes and tutor answers based on your material. Account deletion wipes everything irreversibly after a 30-day grace period — you can cancel it and come back."
			},
			"pricing": {
				"q": "How much does it cost?",
				"a": "Free — forever: 5 AI generations a day, up to 10 materials. Plus ({plusPrice}/mo) raises the limit to 50 generations a day and a 5 GB library. Pro ({proPrice}/mo) removes the generation limit."
			},
			"youtube": {
				"q": "Can I upload YouTube videos?",
				"a": "Yes. Paste a link — StudyHub pulls the transcript and builds notes, flashcards and a quiz from it. Any web page works the same way; audio and video files can be uploaded directly."
			},
			"mobile": {
				"q": "Does it work on mobile?",
				"a": "Yes. Native apps for iOS and Android with the same backend as web — synced offline. Lecture recording keeps running with the screen locked."
			},
			"errors": {
				"q": "What if the AI is wrong?",
				"a": "Tutor answers and notes show their source — the exact fragment of your material. You can see where it came from and verify it in one click."
			},
			"cancel": {
				"q": "Can I cancel my subscription?",
				"a": "Yes, any time. Plus or Pro access stays until the end of the paid period — no hidden charges or retention games."
			}
		},
		"contact": {
			"title": "Still have questions?",
			"lead": "Write to us — we reply personally and usually within one business day.",
			"emailLabel": "Email",
			"telegramLabel": "Telegram support",
			"legalNav": "Legal documents"
		},
		"noteNoSale": "Method · data · support"
	},
	"finalCta": {
		"title": "Ready to try it?",
		"lead": "14 days of Plus on us, no credit card.",
		"signup": "Start free",
		"sso": "I already have an account",
		"leadNoSale": "Upload your first material and see the method at work."
	},
	"footer": {
		"tagline": "Your second brain for studying — notes, flashcards, quizzes, chat and a voice tutor in one app.",
		"product": "Product",
		"company": "Company",
		"legal": "Legal",
		"rights": "© 2026 StudyHub",
		"links": {
			"features": "Features",
			"pricing": "Pricing",
			"mobile": "Mobile app",
			"about": "About",
			"support": "Help",
			"privacy": "Privacy Policy",
			"terms": "Terms of Service",
			"ai": "AI disclaimer",
			"cookie": "Cookie Policy"
		}
	},
	"ecosystem": {
		"title": "The IEC ecosystem",
		"subtitle": "StudyHub is part of the educational ecosystem of the International Exchange Center",
		"appStoreAlt": "Download on the App Store",
		"googlePlayAlt": "Get it on Google Play",
		"exchange": "Exchange portal",
		"exchangeDesc": "Connect, read the news, join programmes and contests",
		"courses": "Course platform",
		"coursesDesc": "Study online for unique professions",
		"hero": "IEC Hero",
		"heroDesc": "Chat with AI characters and create your own",
		"travel": "TravelHub",
		"travelDesc": "Find unique travel formats at a good price",
		"career": "IEC Career Lab",
		"careerDesc": "Find unique internship programmes",
		"programs": "International exchange programmes",
		"programsDesc": "Work and study abroad through exchange programmes",
		"prev": "Previous services",
		"next": "Next services"
	}
}
